import {
  CheckCircleTwoTone,
  CloseCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Checkbox,
  Form,
  Input,
  Modal,
  notification,
  Progress,
  Space,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Agent, ClientFeatures } from '../../types';
import { ClientConfig } from '../../types/config';
import { escapeHTML, sanitizeHTML, validatePhoneOverride } from '../../utils';
import { HWButton } from '../shared/StyledButton';
import { AgentOverrides } from './agent-overrides';
import { SendCalendarInviteModal } from './send-calendar-invite-modal';
import { SendMessageModal } from './send-message-modal';

interface AgentFormData {
  agents: Record<string, Agent>;
}

interface AgentSectionProps {
  apiUrl: string;
  clientConfigId: string;
  accessToken: string;
  setError: (error: string | undefined) => void;
  clientFeatures: ClientFeatures;
  clientId: number;
  clientConfig: ClientConfig; // Updated to full ClientConfig type
}

interface MatchedAgent {
  id: string;
  first_name: string;
  last_name: string;
}

interface MissingAgent {
  raw_input: string;
}

interface MatchedAgentsResponse {
  agents: MatchedAgent[];
  missing: MissingAgent[];
}

const MATCH_AGENT_LINES_PER_CHUNK = 10;

function splitAgentTextIntoChunks(text: string): string[] {
  const lines = text.split('\n');
  const chunks: string[] = [];
  for (let i = 0; i < lines.length; i += MATCH_AGENT_LINES_PER_CHUNK) {
    const body = lines.slice(i, i + MATCH_AGENT_LINES_PER_CHUNK).join('\n');
    if (body.trim().length > 0) {
      chunks.push(body);
    }
  }
  return chunks;
}

function mergeMatchedResponses(
  responses: MatchedAgentsResponse[],
): MatchedAgentsResponse {
  const agentsById = new Map<string, MatchedAgent>();
  const missingByKey = new Map<string, MissingAgent>();

  for (const data of responses) {
    for (const agent of data.agents ?? []) {
      agentsById.set(agent.id, agent);
    }
    for (const m of data.missing ?? []) {
      const key = m.raw_input.trim();
      if (!key) {
        continue;
      }
      if (!missingByKey.has(key)) {
        missingByKey.set(key, { raw_input: key });
      }
    }
  }

  return {
    agents: Array.from(agentsById.values()),
    missing: Array.from(missingByKey.values()),
  };
}

function chunkProgressLabel(chunkIndex: number, totalChunks: number): string {
  return totalChunks > 1 ? `Chunk ${chunkIndex} of ${totalChunks}: ` : '';
}

async function parseMatchAgentsErrorResponse(
  response: Response,
): Promise<string> {
  const raw = await response.text();
  if (!raw.trim()) {
    return response.statusText || `HTTP ${response.status}`;
  }
  try {
    const j = JSON.parse(raw) as {
      detail?: unknown;
      message?: string;
      error?: string;
    };
    if (typeof j.detail === 'string') {
      return j.detail;
    }
    if (Array.isArray(j.detail)) {
      return j.detail
        .map((d: unknown) =>
          typeof d === 'object' && d !== null && 'msg' in d
            ? String((d as { msg: string }).msg)
            : JSON.stringify(d),
        )
        .join('; ');
    }
    return j.message || j.error || raw.slice(0, 300);
  } catch {
    return raw.slice(0, 500);
  }
}

function describeMatchAgentsFailure(
  err: unknown,
  context: {
    chunkIndex: number;
    totalChunks: number;
    httpStatus?: number;
    serverDetail?: string;
  },
): string {
  const prefix = chunkProgressLabel(context.chunkIndex, context.totalChunks);

  if (context.httpStatus === 401 || context.httpStatus === 403) {
    return `${prefix}Access denied (${context.httpStatus}). Sign in again or check permissions.`;
  }

  if (context.httpStatus != null && context.httpStatus >= 500) {
    const detail = context.serverDetail?.trim();
    return `${prefix}The server failed or timed out (${context.httpStatus})${detail ? `: ${detail}` : ''}. Matching uses AI and can exceed default limits—try fewer lines or ask your team to increase timeouts for match-agents.`;
  }

  const msg = (() => {
    if (context.serverDetail?.trim()) {
      return context.serverDetail.trim();
    }
    if (err instanceof Error) {
      return err.message;
    }
    return String(err);
  })();

  const lower = msg.toLowerCase();
  if (
    lower.includes('load failed') ||
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network connection was lost') ||
    lower.includes('internet connection appears to be offline') ||
    lower.includes('econnreset')
  ) {
    return `${prefix}The connection was lost before the server finished. Matching can take a long time; the browser or gateway may close slow requests. Try again, use a smaller paste, or raise timeouts for /api/onboarding/match-agents.`;
  }

  if (err instanceof Error && err.name === 'AbortError') {
    return `${prefix}The request was cancelled.`;
  }

  if (
    lower.includes('unexpected token') ||
    lower.includes('unexpected end of json') ||
    (lower.includes('json') && lower.includes('parse'))
  ) {
    return `${prefix}The server returned data we could not read. Try again or contact support if this continues.`;
  }

  return `${prefix}${msg}`;
}

export function AgentSection({
  apiUrl,
  clientConfigId,
  accessToken,
  setError,
  clientFeatures,
  clientId,
  clientConfig,
}: AgentSectionProps) {
  const navigate = useNavigate();
  const [form] = Form.useForm<AgentFormData>();
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isTouched, setIsTouched] = useState(false);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent>();
  const [isMatchModalVisible, setIsMatchModalVisible] = useState(false);
  const [rawAgentText, setRawAgentText] = useState('');
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchChunkTotal, setMatchChunkTotal] = useState(0);
  const [matchChunkCompleted, setMatchChunkCompleted] = useState(0);
  const [matchAgentsError, setMatchAgentsError] = useState<string>();
  const [isSendMessageModalVisible, setIsSendMessageModalVisible] =
    useState(false);
  const [isCalendarInviteModalVisible, setIsCalendarInviteModalVisible] =
    useState(false);

  const matchAgentsFromText = async (
    action: 'onboarding' | 'hw_enabled' | 'isa',
  ) => {
    const chunks = splitAgentTextIntoChunks(rawAgentText);
    if (chunks.length === 0) {
      const emptyMsg =
        'Paste at least one non-empty line of agent text to match.';
      setMatchAgentsError(emptyMsg);
      setError(emptyMsg);
      return;
    }

    setMatchChunkTotal(chunks.length);
    setMatchChunkCompleted(0);
    setMatchLoading(true);
    setError(undefined);
    setMatchAgentsError(undefined);

    try {
      const perChunkResults: MatchedAgentsResponse[] = [];

      for (let i = 0; i < chunks.length; i++) {
        const chunkIndex = i + 1;
        let response: Response;
        try {
          response = await fetch(`${apiUrl}/api/onboarding/match-agents`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              client_id: clientId,
              text: chunks[i],
            }),
          });
        } catch (networkErr) {
          throw new Error(
            describeMatchAgentsFailure(networkErr, {
              chunkIndex,
              totalChunks: chunks.length,
            }),
          );
        }

        if (!response.ok) {
          const serverDetail = await parseMatchAgentsErrorResponse(response);
          throw new Error(
            describeMatchAgentsFailure(new Error(serverDetail), {
              chunkIndex,
              totalChunks: chunks.length,
              httpStatus: response.status,
              serverDetail,
            }),
          );
        }

        let data: MatchedAgentsResponse;
        try {
          data = (await response.json()) as MatchedAgentsResponse;
        } catch (parseErr) {
          throw new Error(
            describeMatchAgentsFailure(parseErr, {
              chunkIndex,
              totalChunks: chunks.length,
            }),
          );
        }
        perChunkResults.push(data);
        setMatchChunkCompleted(chunkIndex);
      }

      const data = mergeMatchedResponses(perChunkResults);

      if (data.agents && data.agents.length > 0) {
        const matchedIds = data.agents.map(agent => agent.id);

        if (action === 'onboarding') {
          setSelectedAgentIds(prevSelected =>
            Array.from(new Set([...prevSelected, ...matchedIds])),
          );
        } else if (action === 'hw_enabled') {
          form.setFieldsValue({
            agents: {
              ...form.getFieldValue('agents'),
              ...Object.fromEntries(
                matchedIds.map(id => [
                  id,
                  {
                    ...form.getFieldValue(['agents', id]),
                    is_hw_enabled: true,
                  },
                ]),
              ),
            },
          });
        } else if (action === 'isa') {
          form.setFieldsValue({
            agents: {
              ...form.getFieldValue('agents'),
              ...Object.fromEntries(
                matchedIds.map(id => [
                  id,
                  { ...form.getFieldValue(['agents', id]), is_isa: true },
                ]),
              ),
            },
          });
        }
      }

      if (data.missing && data.missing.length > 0) {
        const missingNames = data.missing.map(m => m.raw_input).join(', ');

        notification.info({
          message: 'Some agents were not found',
          description: (
            <div>
              <p>The following agents could not be matched in the system:</p>
              <p>{missingNames}</p>
            </div>
          ),
          duration: 0,
          icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
        });
      }

      setIsMatchModalVisible(false);
      setRawAgentText('');
      setMatchAgentsError(undefined);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : describeMatchAgentsFailure(err, {
              chunkIndex: 1,
              totalChunks: chunks.length,
            });
      setMatchAgentsError(message);
      setError(message);
    } finally {
      setMatchLoading(false);
      setMatchChunkTotal(0);
      setMatchChunkCompleted(0);
    }
  };

  const fetchAgents = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch(`${apiUrl}/api/settings/${clientConfigId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${data.message}`);
      }

      // Access agents from data.agents
      const sortedAgents = data.agents.sort((a: Agent, b: Agent) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setAgents(sortedAgents);
      const agentsRecord = sortedAgents.reduce(
        (acc: Record<string, Agent>, agent: Agent) => {
          acc[agent.id] = agent;
          return acc;
        },
        {},
      );
      form.setFieldsValue({ agents: agentsRecord });
    } catch (err) {
      setError(`${err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async (values: AgentFormData) => {
    setLoading(true);
    setError(undefined);
    try {
      const changedAgents = Object.keys(values.agents)
        .filter(agentId => {
          const agent = values.agents[agentId];
          const originalAgent = agents.find(a => a.id === agentId);
          return (
            originalAgent &&
            (agent.is_hw_enabled !== originalAgent.is_hw_enabled ||
              agent.phone_override !== originalAgent.phone_override ||
              agent.is_isa !== originalAgent.is_isa ||
              agent.hw_internal_notes !== originalAgent.hw_internal_notes)
          );
        })
        .map(agentId => {
          const agent = values.agents[agentId];
          return {
            id: agentId,
            is_hw_enabled: agent.is_hw_enabled,
            phone_override: agent.phone_override,
            is_isa: agent.is_isa,
            hw_internal_notes: agent.hw_internal_notes,
          };
        });

      if (changedAgents.length === 0) {
        setLoading(false);
        setIsTouched(false);
        return;
      }

      // Check if any agent is being enabled for House Whisper and has internal notes
      // Find all agents being enabled with notes
      const agentsWithNotes = changedAgents.filter(
        agent =>
          agent.is_hw_enabled &&
          agents.find(a => a.id === agent.id)?.hw_internal_notes,
      );

      if (agentsWithNotes.length > 0) {
        // Create a formatted list of agents with their notes
        const agentsList = agentsWithNotes
          .map(agentWithNote => {
            const agent = agents.find(a => a.id === agentWithNote.id);
            return `• ${escapeHTML(agent?.first_name || '')} ${escapeHTML(agent?.last_name || '')}: "${escapeHTML(agent?.hw_internal_notes || '')}"`;
          })
          .join('<br>'); // Use <br> tags for line breaks in the modal

        // Show confirmation modal with all agents that have notes
        const confirmEnable = await new Promise<boolean>(resolve => {
          Modal.confirm({
            title: 'Enabling House Whisper with Notes',
            content: (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(
                    `Are you sure you want to enable House Whisper for the following agents with notes?<br>${agentsList}`,
                  ),
                }}
              />
            ),
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
            width: 500,
          });
        });

        if (!confirmEnable) {
          setLoading(false);
          return;
        }
      }

      const response = await fetch(`${apiUrl}/api/agents/${clientConfigId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(changedAgents),
      });

      if (!response.ok) {
        throw new Error('Failed to update agent settings');
      }

      await fetchAgents();
      setIsTouched(false);
    } catch (error) {
      console.error('Error updating agent settings:', error);
      setError(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  const createOnboardingFlow = async () => {
    setLoading(true);
    setError(undefined);
    try {
      // Check if any selected agent has internal notes
      const agentsWithNotes = selectedAgentIds
        .filter(agentId => {
          const agent = agents.find(a => a.id === agentId);
          return agent?.hw_internal_notes;
        })
        .map(agentId => agents.find(a => a.id === agentId))
        .filter(Boolean) as Agent[];

      if (agentsWithNotes.length > 0) {
        // Create a formatted list of agents with their notes
        const agentsList = agentsWithNotes
          .map(agent => {
            return `• ${escapeHTML(agent.first_name || '')} ${escapeHTML(agent.last_name || '')}: "${escapeHTML(agent.hw_internal_notes || '')}"`;
          })
          .join('<br>');

        // Show confirmation modal with all agents that have notes
        const confirmContinue = await new Promise<boolean>(resolve => {
          Modal.confirm({
            title: 'Creating Onboarding with Agents That Have Notes',
            content: (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(
                    `Are you sure you want to create onboarding for the following agents with notes?<br>${agentsList}`,
                  ),
                }}
              />
            ),
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
            width: 500,
          });
        });

        if (!confirmContinue) {
          setLoading(false);
          return;
        }
      }

      const response = await fetch(`${apiUrl}/api/onboarding/flow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          client_config_id: clientConfigId,
          agent_ids: selectedAgentIds,
        }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }

      navigate(`/onboardingNew/${clientId}`);
    } catch (error) {
      console.error('Error updating agent settings:', error);
      setError(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const tableData = agents.map(agent => ({
      Name: `${agent.first_name} ${agent.last_name}`,
      Email: agent.email || null,
      Phone: agent.phone || null,
      ISA: agent.is_isa ? 'Yes' : '',
      'Onboard to HouseWhisper': agent.is_hw_enabled ? 'Yes' : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(tableData);

    // Adjust column widths for better readability
    const columnWidths = [
      { wch: 20 }, // Name
      { wch: 30 }, // Email
      { wch: 15 }, // Phone
      { wch: 10 }, // ISA
      { wch: 25 }, // Onboard to HouseWhisper
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Agents');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    // Use the client's friendly name for the file name
    const fileName = `${clientConfig.friendly_name} Agent List.xlsx`;
    saveAs(blob, fileName);
  };

  const renderTitle = (title: string, tooltip: string) => (
    <Space>
      {title}
      <Typography.Text style={{ fontSize: '14px' }} type="secondary">
        <QuestionCircleOutlined style={{ cursor: 'help' }} title={tooltip} />
      </Typography.Text>
    </Space>
  );

  const rowSelection = {
    selectedRowKeys: selectedAgentIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedAgentIds(selectedRowKeys as string[]);
    },
  };

  const columns: ColumnsType<Agent> = [
    {
      title: 'Name',
      key: 'name',
      width: '180px',
      render: (_: unknown, record: Agent) => (
        <Space>
          <Typography.Link
            href={`/agentSms/${clientId}?agentId=${record.id}`}
            target="_blank"
          >
            {record.first_name} {record.last_name}
          </Typography.Link>
          <Typography.Link onClick={() => setSelectedAgent(record)}>
            <InfoCircleOutlined />
          </Typography.Link>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '200px',
      render: (_: string, record: Agent) => (
        <Typography.Link href={`mailto:${record.email || ''}`}>
          <Typography.Text>{record.email || '-'}</Typography.Text>
        </Typography.Link>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: '130px',
      render: (_: string, record: Agent) => (
        <Typography.Link href={`tel:${record.phone || ''}`}>
          <Typography.Text>{record.phone || '-'}</Typography.Text>
        </Typography.Link>
      ),
    },
    {
      title: renderTitle(
        'Phone Override',
        'Override the phone number for this agent',
      ),
      dataIndex: 'phone_override',
      key: 'phone_override',
      width: '180px',
      render: (_: string, record: Agent) => (
        <Form.Item
          name={['agents', record.id, 'phone_override']}
          rules={[
            {
              validator: (_rule, value) => {
                const err = validatePhoneOverride(value, record.dialer_phone);
                return err ? Promise.reject(new Error(err)) : Promise.resolve();
              },
            },
          ]}
          style={{ margin: 0 }}
        >
          <Input allowClear placeholder="+1XXXXXXXXXX" />
        </Form.Item>
      ),
    },
    {
      title: renderTitle(
        'House Whisper Enabled',
        'Enable House Whisper features for this agent',
      ),
      dataIndex: 'is_hw_enabled',
      key: 'is_hw_enabled',
      width: '130px',
      align: 'center',
      render: (_: boolean, record: Agent) => (
        <Form.Item
          name={['agents', record.id, 'is_hw_enabled']}
          style={{ margin: 0, textAlign: 'center' }}
          valuePropName="checked"
        >
          <Checkbox title="Enable House Whisper features for this agent" />
        </Form.Item>
      ),
    },
    {
      title: renderTitle('Calendar Enabled', 'Does this agent have calendar'),
      dataIndex: 'has_calander_setup',
      key: 'has_calander_setup',
      width: '130px',
      align: 'center',
      render: (_: boolean, record: Agent) => (
        <Typography.Text>
          {record.has_calander_setup ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <CloseCircleOutlined style={{ color: 'red' }} />
          )}
        </Typography.Text>
      ),
    },
    {
      title: renderTitle('ISA', 'Inside Sales Agent'),
      dataIndex: 'is_isa',
      key: 'is_isa',
      width: '100px',
      align: 'center',
      render: (_: boolean, record: Agent) => (
        <Form.Item
          name={['agents', record.id, 'is_isa']}
          style={{ margin: 0, textAlign: 'center' }}
          valuePropName="checked"
        >
          <Checkbox title="Mark this agent as an Inside Sales Agent" />
        </Form.Item>
      ),
    },
    {
      title: renderTitle(
        'Internal Notes',
        'House Whisper internal notes for this agent',
      ),
      dataIndex: 'hw_internal_notes',
      key: 'hw_internal_notes',
      width: '250px',
      render: (_: string, record: Agent) => (
        <Form.Item
          name={['agents', record.id, 'hw_internal_notes']}
          style={{ margin: 0 }}
        >
          <Input.TextArea
            placeholder="Add notes, e.g., told us to stop texting"
            rows={2}
            title="Add internal notes for this agent"
          />
        </Form.Item>
      ),
    },
  ];

  useEffect(() => {
    void fetchAgents();
  }, []);
  return (
    <div
      style={{ textAlign: 'left', backgroundColor: 'white', padding: '20px' }}
    >
      <Typography.Title level={2}>Agent Configuration</Typography.Title>
      <Typography.Paragraph>
        Configure House Whisper settings for each agent. Enable or disable House
        Whisper features and set phone number overrides as needed.
      </Typography.Paragraph>
      <Form
        disabled={loading}
        form={form}
        // initialValues={{ agents }}
        onFinish={values => {
          void handleFinish(values);
        }}
        onValuesChange={() => setIsTouched(true)}
      >
        <Table
          columns={columns}
          dataSource={agents}
          pagination={false}
          rowKey="id"
          rowSelection={rowSelection}
        />
        <Space style={{ marginTop: '20px' }}>
          <HWButton
            disabled={
              !isTouched ||
              form.getFieldsError().some(({ errors }) => errors.length > 0)
            }
            htmlType="submit"
            type="primary"
          >
            Save
          </HWButton>
          <HWButton
            disabled={selectedAgentIds.length === 0}
            loading={loading}
            onClick={() => {
              void createOnboardingFlow();
            }}
            type="primary"
          >
            Create Onboarding
          </HWButton>
          <HWButton
            onClick={() => {
              setMatchAgentsError(undefined);
              setIsMatchModalVisible(true);
            }}
            type="default"
          >
            Match Agents
          </HWButton>
          <HWButton onClick={exportToExcel} type="default">
            Save Agent List to Excel
          </HWButton>
          <HWButton
            disabled={selectedAgentIds.length === 0}
            onClick={() => setIsSendMessageModalVisible(true)}
            type="primary"
          >
            Send Message
          </HWButton>
          <HWButton
            disabled={selectedAgentIds.length === 0}
            onClick={() => setIsCalendarInviteModalVisible(true)}
            type="primary"
          >
            Send Calendar Invites
          </HWButton>
        </Space>
      </Form>

      {/* Match Agents Modal */}
      <Modal
        closable={!matchLoading}
        footer={
          <Space>
            <HWButton
              disabled={matchLoading}
              key="onboarding"
              onClick={() => {
                void matchAgentsFromText('onboarding');
              }}
              title="Set agents for onboarding based on the provided text"
              type="primary"
            >
              Set Onboarding
            </HWButton>
            <HWButton
              disabled={matchLoading}
              key="hw_enabled"
              onClick={() => {
                void matchAgentsFromText('hw_enabled');
              }}
              title="Mark matched agents as House Whisper enabled"
              type="default"
            >
              Select HW Enabled
            </HWButton>
            <HWButton
              disabled={matchLoading}
              key="isa"
              onClick={() => {
                void matchAgentsFromText('isa');
              }}
              title="Mark matched agents as Inside Sales Agents (ISA)"
              type="default"
            >
              Select ISA
            </HWButton>
          </Space>
        }
        maskClosable={!matchLoading}
        onCancel={() => {
          if (matchLoading) {
            return;
          }
          setMatchAgentsError(undefined);
          setIsMatchModalVisible(false);
        }}
        title="Match Agents from Text"
        visible={isMatchModalVisible}
        width={600}
      >
        <div style={{ position: 'relative' }}>
          {matchLoading && matchChunkTotal > 0 ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: 12,
                padding: 24,
                background: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 8,
              }}
            >
              <Typography.Text strong>
                {matchChunkCompleted === 0
                  ? `Starting: chunk 1 of ${matchChunkTotal}…`
                  : `${matchChunkCompleted} of ${matchChunkTotal} chunks complete`}
              </Typography.Text>
              <Progress
                percent={Math.round(
                  (matchChunkCompleted / matchChunkTotal) * 100,
                )}
              />
              <Typography.Text type="secondary">
                Matching can take a while. Please wait.
              </Typography.Text>
            </div>
          ) : null}
          <Typography.Paragraph>
            Paste a list of agent names, emails, or other identifying
            information below. The system will attempt to match them with agents
            in the database.
          </Typography.Paragraph>
          {matchAgentsError ? (
            <Alert
              closable
              description={matchAgentsError}
              message="Could not match agents"
              onClose={() => setMatchAgentsError(undefined)}
              showIcon
              style={{ marginBottom: 12 }}
              type="error"
            />
          ) : null}
          <Input.TextArea
            disabled={matchLoading}
            onChange={e => setRawAgentText(e.target.value)}
            placeholder="Example:
John Smith jsmith@example.com
Mary Johnson
Tom Wilson (tomw@domain.com)
..."
            rows={10}
            value={rawAgentText}
          />
        </div>
      </Modal>

      {selectedAgent !== undefined && (
        <AgentOverrides
          accessToken={accessToken}
          agent={selectedAgent}
          apiUrl={apiUrl}
          clientFeatures={clientFeatures}
          clientId={clientConfigId}
          onAgentUpdated={fetchAgents}
          onClose={() => setSelectedAgent(undefined)}
          visible={true}
        />
      )}

      <SendMessageModal
        apiUrl={apiUrl}
        clientConfig={clientConfig}
        onClose={() => setIsSendMessageModalVisible(false)}
        selectedAgents={agents.filter(agent =>
          selectedAgentIds.includes(agent.id),
        )}
        visible={isSendMessageModalVisible}
      />

      <SendCalendarInviteModal
        apiUrl={apiUrl}
        clientId={clientConfigId}
        onClose={() => setIsCalendarInviteModalVisible(false)}
        selectedAgents={agents.filter(agent =>
          selectedAgentIds.includes(agent.id),
        )}
        visible={isCalendarInviteModalVisible}
      />
    </div>
  );
}
